import Wallet from '@mui/icons-material/AccountBalanceWalletOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FilterIcon from '@mui/icons-material/TuneOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { es } from 'date-fns/locale';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import AlertBox from '@/components/AlertBox';
import GenericDrawer from '@/components/GenericDrawer';
import { AgaveDataGrid } from '@/components/GenericTable/DataGridStyled';
import RangeDatePicker from '@/components/RangeDatePicker/RangeDatePicker';
import { RootState } from '@/redux/store';

import EditProfile from '../EditProfile';
import RechargeBalance from '../RechargeBalance';
import { invoicesColumns } from './invoices-columns';
import useInvoicesSection from './useInvoicesSection';

const InvoicesSection = () => {
  const { profile } = useSelector((state: RootState) => state.parcel);
  const theme = useTheme();
  const hook = useInvoicesSection();

  const needInvoices = profile.data?.company.requireInvoice ?? false;
  const profileData = !!(
    profile.data?.company.rfc &&
    profile.data?.company.legalName &&
    profile.data?.company.taxRegime &&
    profile.data?.company.cfdiUse
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      {!needInvoices && (
        <Box sx={{ width: '100%', marginBottom: '0.5rem' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.875rem',
              padding: '0.875rem 1rem',
              borderRadius: '12px',
              backgroundColor: '#D5EEE9',
              color: theme.palette.secondary.main,
              boxShadow: '0px 1px 2px rgba(18, 52, 74, 0.06)',
            }}
          >
            <Box
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '9px',
                backgroundColor: '#BFE3DC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: '1.1rem', color: 'inherit' }} />
            </Box>
            <Typography variant="body1" sx={{ color: 'inherit' }}>
              La facturación no está disponible en tu cuenta.{' '}
              <Box
                component="span"
                onClick={() => hook.setEditProfile(true)}
                sx={{
                  fontWeight: 700,
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                  cursor: 'pointer',
                }}
              >
                Actívala desde tu perfil.
              </Box>
            </Typography>
          </Box>
        </Box>
      )}

      {needInvoices && !profileData && (
        <Box sx={{ margin: '2rem auto', maxWidth: '768px' }}>
          <AlertBox type="warning" direction="row">
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}
            >
              <Typography variant="body1" textAlign={'center'}>
                Es necesario que completes tu información fiscal, de lo contrario tus facturas se
                crearán a público general.{' '}
                <span
                  style={{
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                  onClick={() => hook.setEditProfile(true)}
                >
                  Actualiza tus datos aquí
                </span>
              </Typography>
            </Box>
          </AlertBox>
        </Box>
      )}

      {needInvoices && Array.isArray(hook.invoices.data) && hook.invoices.data.length === 0 ? (
        <Box sx={{ margin: '2rem auto', maxWidth: '768px' }}>
          <AlertBox type="generic" direction="row">
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}
            >
              <Typography variant="body1" textAlign={'center'}>
                Realiza tu primer envío{' '}
                <Link
                  to="/"
                  style={{
                    textDecoration: 'none',
                    fontWeight: '600',
                    cursor: 'pointer',
                    color: theme.palette.secondary.main,
                  }}
                >
                  aquí
                </Link>
              </Typography>
            </Box>
          </AlertBox>
        </Box>
      ) : (
        <Box sx={{ width: '100%', marginBottom: '1.5rem' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.875rem',
              padding: '0.875rem 1rem',
              borderRadius: '12px',
              backgroundColor: '#D9EDDF',
              color: theme.palette.primary.main,
              boxShadow: '0px 1px 2px rgba(18, 52, 74, 0.06)',
            }}
          >
            <Box
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '9px',
                backgroundColor: '#C3E4C9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: '1.1rem', color: 'inherit' }} />
            </Box>
            <Typography variant="body1" sx={{ color: 'inherit', fontWeight: 500 }}>
              Las facturas se generan en un plazo de hasta 2 horas después de la operación.
            </Typography>
          </Box>
        </Box>
      )}

      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0',
        }}
      >
        <Box sx={{ width: '100%', padding: '1rem', display: 'flex', justifyContent: 'end' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <RangeDatePicker
              untilToday
              noPadding
              startLabel="Desde"
              endLabel="Hasta"
              onChange={hook.handleDateChange}
            />
          </LocalizationProvider>
        </Box>

        <Box sx={{ overflowX: 'auto', width: '100%' }}>
          <Box sx={{ minWidth: '700px' }}>
            <AgaveDataGrid
              columns={invoicesColumns()}
              rows={hook.invoices.data?.invoices || []}
              localeText={{
                noRowsLabel: 'No hay facturas disponibles',
              }}
              rowCount={
                hook.invoices.data?.totalPages
                  ? hook.invoices.data.totalPages * (hook.pagination.filters.page_size as number)
                  : 0
              }
              loading={hook.invoices.loading}
              disableRowSelectionOnClick={true}
              disableCursorPointer={true}
              getRowId={(row) => row.invoiceId}
              hideFooterSelectedRowCount={true}
              paginationModel={{
                page: hook.pagination.filters.page as number,
                pageSize: hook.pagination.filters.page_size as number,
              }}
              disableColumnMenu={true}
              paginationMode="server"
              onPaginationModelChange={hook.pagination.onPaginationModelChange}
            />
          </Box>
        </Box>
      </Paper>

      <GenericDrawer
        open={hook.editProfile}
        onClose={() => hook.setEditProfile(false)}
        title="Mis datos"
        disableEscapeKeyDown={true}
        iconTitle={<FilterIcon color="primary" />}
      >
        <EditProfile openCompany />
      </GenericDrawer>
      <GenericDrawer
        open={hook.chargeDrawer}
        onClose={() => hook.setChargeDrawer(false)}
        title={'Recargar saldo'}
        disableEscapeKeyDown={true}
        iconTitle={<Wallet color="primary" />}
      >
        <RechargeBalance />
      </GenericDrawer>
    </Box>
  );
};

export default InvoicesSection;
